import RecordRTC, { State } from "recordrtc";
import { v4 as uuidv4 } from "uuid";
import { ReturnData } from "src/types";

export let Recorder: {
  startRecording: () => Promise<ReturnData>;
  stopRecording: (getBlob?: boolean) => Promise<ReturnData>;
  pauseRecording: Function;
  resumeRecording: Function;
  addOnDataListner: (hook: Function) => string;
  removeDataListner: (hookRef: string) => void;
  setVideoStreams: (streams: Array<MediaStream>) => void;
  addMediaStreams: (streams: Array<MediaStream>) => void;
  getStatus: () => Promise<State> | undefined;
};

type RecorderConfigType = {
  onData?: Function;
  initStream: MediaStream;
};

type HookType = {
  ref: string;
  hook: Function;
};

export const createRecorderInstance = (config?: RecorderConfigType) => {
  console.log("Recorder: Inited");

  let _Recorder: RecordRTC.RecordRTCPromisesHandler | undefined;

  // Main state
  const state: {
    isRecording: boolean;
    mediaStreams: Array<MediaStream>;
  } = {
    isRecording: false,
    mediaStreams: [],
  };

  const initRecorder = () => {
    if (!state.mediaStreams.length) return { success: false, message: "Nenhuma media stream encontrada." };

    // @ts-ignore: Unreachable code error
    _Recorder = new RecordRTC.RecordRTCPromisesHandler(state.mediaStreams, {
      type: "video",
      mimeType: "video/webm;codecs=vp8",
      recorderType: RecordRTC.MultiStreamRecorder,
      videoBitsPerSecond: 128000, // NORMAL
      disableLogs: false,
      timeSlice: 5000,
      ondataavailable: onDataAvailable,
    });

    return { success: true, instance: _Recorder };
  };

  // Hooks
  const activeHooks: Array<HookType> = [];
  async function onDataAvailable(blob: Blob) {
    if (state.isRecording) {
      activeHooks.forEach((h) => {
        h.hook(blob);
      });
    } else {
      console.log("Recorder: OnDataAvailable called without recording in progress.");
    }
  }

  const addOnDataListner = (hook: Function) => {
    const newHook = {
      ref: uuidv4(),
      hook,
    };

    activeHooks.push(newHook);

    return newHook.ref;
  };

  const removeDataListner = (ref: string) => {
    const foundHookIndex = activeHooks.findIndex((h) => h.ref === ref);

    return foundHookIndex > -1 ? activeHooks.splice(foundHookIndex, 1).length > 0 : false;
  };

  // Methods
  const startRecording = async () => {
    // Create
    if (!_Recorder) {
      const { success, message, instance } = await initRecorder();

      if (!success) return { success, message };

      _Recorder!.startRecording();

      // state.status = "RECORDING";
    } else {
      // Reset and Record
      _Recorder.reset();
      _Recorder.startRecording();

      // Get recorder
      const internalRecorder = await _Recorder.getInternalRecorder();

      // Reset video tracks & add audio tracks
      if (internalRecorder instanceof RecordRTC.MultiStreamRecorder) {
        internalRecorder.resetVideoStreams(state.mediaStreams);

        const audioTracks: Array<MediaStreamTrack> = [];

        state.mediaStreams.forEach((s) => s.getAudioTracks().forEach((t) => audioTracks.push(t)));

        const audioStream = new MediaStream(audioTracks);
        internalRecorder.addStreams([audioStream]);
      }
    }

    // Init recording
    state.isRecording = true;

    return {
      success: true,
    };
  };

  const resumeRecording = () => {
    if (_Recorder) {
      state.isRecording = true;
      _Recorder.resumeRecording();
    }
  };

  const pauseRecording = () => {
    if (_Recorder) {
      state.isRecording = false;
      _Recorder.pauseRecording();
    }
  };

  const stopRecording = async (getBlob = false) => {
    if (_Recorder) {
      try {
        let data: any;

        const recorderTrueState: RecordRTC.State = await _Recorder.getState();

        // Mark recording stoped
        if (recorderTrueState === "recording") await _Recorder.stopRecording();
        state.isRecording = false;

        if (getBlob) {
          data = await _Recorder.getBlob();
        }

        // Reset instance
        await _Recorder.reset();
        _Recorder = undefined;
        state.mediaStreams = [];

        return {
          success: true,
          data,
        };
      } catch (ex: any) {
        return {
          success: false,
          message: "Falha ao finalizar gravação" + JSON.stringify(ex.message),
        };
      }
    }

    return {
      success: false,
      message: "Erro ao parar gravação: Gravador não inicializado",
    };
  };

  const setVideoStreams = async (streams: Array<MediaStream>) => {
    if (_Recorder) {
      const internalRecorder = await _Recorder.getInternalRecorder();

      if (internalRecorder instanceof RecordRTC.MultiStreamRecorder) {
        internalRecorder.resetVideoStreams(streams);
      }

      return { success: true };
    }

    return { success: false, message: "Falha ao setar as streams" };
  };

  const addMediaStreams = async (streams: Array<MediaStream>) => {
    if (!streams) return;

    if (_Recorder) {
      const internalRecorder = await _Recorder.getInternalRecorder();
      if (internalRecorder instanceof RecordRTC.MultiStreamRecorder) {
        internalRecorder.addStreams(streams);
      }
    } else state.mediaStreams = state.mediaStreams.concat(streams);
  };

  return (Recorder = {
    startRecording,
    stopRecording,
    pauseRecording,
    addOnDataListner,
    removeDataListner,
    resumeRecording,
    addMediaStreams,
    setVideoStreams,

    getStatus: () => _Recorder?.getState(),
  });
};

export default createRecorderInstance;
